import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import {
  Home, Clock, Bell, LogIn, LogOut, RefreshCw, X,
  Calendar, FileText, Users, Zap, MapPin, Star
} from "lucide-react";
import { toast } from "sonner";

const TABS = [
  { id: "home", icon: Home, label: "Home" },
  { id: "time", icon: Clock, label: "Time" }
];

export default function HelperMobilePage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("home");
  const [loading, setLoading] = useState(true);

  const [attendance, setAttendance] = useState(null);
  const [roster, setRoster] = useState(null);
  const [assignments, setAssignments] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [points, setPoints] = useState({});

  const [punchLoading, setPunchLoading] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [attRes, rosterRes, assignRes, notifRes, pointsRes] = await Promise.all([
        api.get("/attendance/my-today").catch(() => ({ data: {} })),
        api.get("/rosters/my-roster").catch(() => ({ data: {} })),
        api.get("/assignments/my-assignments").catch(() => ({ data: { assignments: [] } })),
        api.get("/communications?type=announcement&limit=5").catch(() => ({ data: { items: [] } })),
        api.get("/gamification/points/my-points?period=today").catch(() => ({ data: {} }))
      ]);

      const attData = attRes.data || {};
      setAttendance({ ...attData.attendance, can_punch_in: attData.can_punch_in, can_punch_out: attData.can_punch_out });
      setRoster(rosterRes.data || null);
      setAssignments(assignRes.data.assignments || []);
      setNotifications(notifRes.data.items || []);
      setPoints(pointsRes.data || {});
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handlePunch = async (type) => {
    setPunchLoading(true);
    toast.info("Capturing location...");
    try {
      const loc = await new Promise((resolve, reject) => {
        if (!navigator.geolocation) return reject(new Error("Geolocation not supported"));
        navigator.geolocation.getCurrentPosition(
          pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          err => reject(err),
          { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
        );
      });
      await api.post(`/attendance/punch-${type}`, { gps_lat: loc.lat, gps_lng: loc.lng });
      toast.success(type === "in" ? "Punched In!" : "Punched Out!");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || `Punch ${type} failed`);
    } finally {
      setPunchLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 pb-20" data-testid="helper-mobile-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white px-4 py-4 sticky top-0 z-40">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-lg font-bold" data-testid="helper-greeting">Hi, {user?.full_name?.split(" ")[0] || "Helper"}!</div>
            <div className="text-xs text-teal-200">
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowNotifications(true)} className="relative p-2 bg-white/20 rounded-full" data-testid="helper-notifications-btn">
              <Bell size={20} />
              {notifications.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-[10px] flex items-center justify-center font-bold">{notifications.length}</span>
              )}
            </button>
            <button onClick={() => { logout(); navigate("/login"); }} className="p-2 bg-white/20 rounded-full" data-testid="helper-logout-btn">
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="px-4 py-4">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw size={32} className="animate-spin text-teal-500" />
          </div>
        ) : (
          <>
            {/* HOME TAB */}
            {activeTab === "home" && (
              <div className="space-y-4" data-testid="helper-home-tab">
                {/* Attendance Quick Status */}
                <div className="bg-white rounded-xl p-4 shadow-sm" data-testid="helper-attendance-status">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-xs font-bold text-slate-500 uppercase">Today's Status</div>
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                      attendance?.punch_in_time ? (attendance?.punch_out_time ? "bg-slate-100 text-slate-600" : "bg-green-100 text-green-700") : "bg-yellow-100 text-yellow-700"
                    }`}>
                      {attendance?.punch_in_time ? (attendance?.punch_out_time ? "Day Complete" : "On Duty") : "Not Punched In"}
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-3 rounded-xl text-center ${attendance?.punch_in_time ? "bg-green-50" : "bg-slate-50"}`}>
                      <LogIn size={20} className={`mx-auto mb-1 ${attendance?.punch_in_time ? "text-green-600" : "text-slate-400"}`} />
                      <div className="text-[10px] text-slate-500">Punch In</div>
                      <div className="text-base font-bold text-slate-800">
                        {attendance?.punch_in_time ? new Date(attendance.punch_in_time).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "--:--"}
                      </div>
                    </div>
                    <div className={`p-3 rounded-xl text-center ${attendance?.punch_out_time ? "bg-red-50" : "bg-slate-50"}`}>
                      <LogOut size={20} className={`mx-auto mb-1 ${attendance?.punch_out_time ? "text-red-600" : "text-slate-400"}`} />
                      <div className="text-[10px] text-slate-500">Punch Out</div>
                      <div className="text-base font-bold text-slate-800">
                        {attendance?.punch_out_time ? new Date(attendance.punch_out_time).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "--:--"}
                      </div>
                    </div>
                  </div>
                  {attendance?.can_punch_in && (
                    <button onClick={() => handlePunch("in")} disabled={punchLoading}
                      className="w-full mt-4 py-3 bg-green-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="helper-punch-in-btn">
                      {punchLoading ? <RefreshCw size={20} className="animate-spin" /> : <LogIn size={20} />}
                      {punchLoading ? "Getting Location..." : "Punch In"}
                    </button>
                  )}
                  {attendance?.can_punch_out && (
                    <button onClick={() => handlePunch("out")} disabled={punchLoading}
                      className="w-full mt-4 py-3 bg-red-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="helper-punch-out-btn">
                      {punchLoading ? <RefreshCw size={20} className="animate-spin" /> : <LogOut size={20} />}
                      {punchLoading ? "Getting Location..." : "Punch Out"}
                    </button>
                  )}
                </div>

                {/* Points */}
                <div className="bg-gradient-to-r from-teal-400 to-emerald-400 rounded-xl p-4 shadow-sm text-white">
                  <div className="flex items-center gap-2 mb-2">
                    <Star size={16} fill="white" />
                    <span className="text-xs font-bold uppercase">Points Earned</span>
                  </div>
                  <div className="text-3xl font-black">
                    +{points.period_points || points.total || 0}
                    <span className="text-sm font-normal ml-2">pts</span>
                  </div>
                </div>

                {/* Today's Roster / Assignment */}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="helper-roster-card">
                  <div className="px-4 py-3 bg-gradient-to-r from-teal-500 to-emerald-500 text-white">
                    <div className="flex items-center gap-2">
                      <Users size={18} />
                      <span className="font-bold">Today's Assignment</span>
                    </div>
                  </div>
                  {roster && roster.roster ? (
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-bold text-slate-800">{roster.roster.feeder_code || "—"}</span>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                          roster.roster.status === "active" ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"
                        }`}>{roster.roster.status || "active"}</span>
                      </div>
                      {roster.roster.vehicle_number && (
                        <div className="text-xs text-slate-500 mb-1">Vehicle: <span className="font-medium">{roster.roster.vehicle_number}</span></div>
                      )}
                      {roster.roster.supervisor_name && (
                        <div className="text-xs text-slate-500">Supervisor: <span className="font-medium">{roster.roster.supervisor_name}</span></div>
                      )}
                      {/* Team Members */}
                      {roster.roster.members && roster.roster.members.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-slate-100">
                          <div className="text-[10px] text-slate-500 font-semibold mb-2">Team Members:</div>
                          <div className="flex flex-wrap gap-1">
                            {roster.roster.members.map((m, i) => (
                              <span key={i} className="px-2 py-1 bg-teal-50 text-teal-700 rounded text-[10px] font-semibold">
                                {m.name || m.employee_name}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="p-6 text-center text-slate-400">
                      <Users size={32} className="mx-auto mb-2 opacity-50" />
                      <div className="text-sm">No roster for today</div>
                      <div className="text-xs mt-1">You'll see your team assignment here</div>
                    </div>
                  )}
                </div>

                {/* Assigned Feeders */}
                {assignments.length > 0 && (
                  <div className="bg-white rounded-xl shadow-sm overflow-hidden" data-testid="helper-feeders-card">
                    <div className="px-4 py-3 bg-slate-50 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <Zap size={16} className="text-teal-500" />
                        <span className="text-sm font-bold text-slate-700">Feeders Assigned</span>
                        <span className="ml-auto text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full font-semibold">{assignments.length}</span>
                      </div>
                    </div>
                    <div className="divide-y divide-slate-100">
                      {assignments.map((a) => (
                        <div key={a.id} className="p-4">
                          <div className="flex items-center justify-between">
                            <div>
                              <div className="font-bold text-slate-800 text-sm">{a.feeder_name || a.feeder_code}</div>
                              <div className="text-xs text-slate-500 mt-0.5">
                                <span className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{a.feeder_code}</span>
                                <span className="mx-1">|</span>
                                <span>{a.circle_name}</span>
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-lg font-bold text-slate-800">{a.dt_count || 0}</div>
                              <div className="text-[10px] text-slate-500">DTs</div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* TIME TAB */}
            {activeTab === "time" && (
              <div className="space-y-4" data-testid="helper-time-tab">
                {/* Attendance Card */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-3">Today</div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className={`p-4 rounded-xl text-center ${attendance?.punch_in_time ? "bg-green-50" : "bg-slate-50"}`}>
                      <LogIn size={24} className={`mx-auto mb-2 ${attendance?.punch_in_time ? "text-green-600" : "text-slate-400"}`} />
                      <div className="text-xs text-slate-500">Punch In</div>
                      <div className="text-lg font-bold text-slate-800" data-testid="helper-time-punch-in">
                        {attendance?.punch_in_time ? new Date(attendance.punch_in_time).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "--:--"}
                      </div>
                    </div>
                    <div className={`p-4 rounded-xl text-center ${attendance?.punch_out_time ? "bg-red-50" : "bg-slate-50"}`}>
                      <LogOut size={24} className={`mx-auto mb-2 ${attendance?.punch_out_time ? "text-red-600" : "text-slate-400"}`} />
                      <div className="text-xs text-slate-500">Punch Out</div>
                      <div className="text-lg font-bold text-slate-800" data-testid="helper-time-punch-out">
                        {attendance?.punch_out_time ? new Date(attendance.punch_out_time).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }) : "--:--"}
                      </div>
                    </div>
                  </div>
                  {attendance?.can_punch_in && (
                    <button onClick={() => handlePunch("in")} disabled={punchLoading}
                      className="w-full mt-4 py-3 bg-green-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="helper-time-punch-in-btn">
                      {punchLoading ? <RefreshCw size={20} className="animate-spin" /> : <LogIn size={20} />}
                      {punchLoading ? "Getting Location..." : "Punch In"}
                    </button>
                  )}
                  {attendance?.can_punch_out && (
                    <button onClick={() => handlePunch("out")} disabled={punchLoading}
                      className="w-full mt-4 py-3 bg-red-500 text-white rounded-xl font-bold flex items-center justify-center gap-2 disabled:opacity-50"
                      data-testid="helper-time-punch-out-btn">
                      {punchLoading ? <RefreshCw size={20} className="animate-spin" /> : <LogOut size={20} />}
                      {punchLoading ? "Getting Location..." : "Punch Out"}
                    </button>
                  )}
                </div>

                {/* Monthly Summary */}
                <div className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="text-xs font-bold text-slate-500 uppercase mb-3">This Month</div>
                  <div className="grid grid-cols-3 gap-3 text-center">
                    <div className="bg-green-50 rounded-lg py-3">
                      <div className="text-xl font-bold text-green-600">{attendance?.month_present || 0}</div>
                      <div className="text-[10px] text-slate-500">Present</div>
                    </div>
                    <div className="bg-yellow-50 rounded-lg py-3">
                      <div className="text-xl font-bold text-yellow-600">{attendance?.month_leave || 0}</div>
                      <div className="text-[10px] text-slate-500">Leave</div>
                    </div>
                    <div className="bg-red-50 rounded-lg py-3">
                      <div className="text-xl font-bold text-red-600">{attendance?.month_absent || 0}</div>
                      <div className="text-[10px] text-slate-500">Absent</div>
                    </div>
                  </div>
                </div>

                {/* Quick Actions */}
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => navigate("/leave")} className="bg-white rounded-xl p-4 shadow-sm text-center hover:bg-slate-50" data-testid="helper-apply-leave-btn">
                    <FileText size={24} className="mx-auto mb-2 text-purple-500" />
                    <div className="text-sm font-semibold text-slate-700">Apply Leave</div>
                  </button>
                  <button onClick={fetchData} className="bg-white rounded-xl p-4 shadow-sm text-center hover:bg-slate-50" data-testid="helper-refresh-btn">
                    <RefreshCw size={24} className="mx-auto mb-2 text-teal-500" />
                    <div className="text-sm font-semibold text-slate-700">Refresh</div>
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-4 py-2 z-50" data-testid="helper-bottom-nav">
        <div className="flex justify-around">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`flex flex-col items-center py-2 px-6 rounded-lg transition ${activeTab === tab.id ? "text-teal-500 bg-teal-50" : "text-slate-500"}`}
              data-testid={`helper-tab-${tab.id}`}>
              <tab.icon size={24} />
              <span className="text-xs font-medium mt-1">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Modals */}
      {showNotifications && <NotificationsModal notifications={notifications} onClose={() => setShowNotifications(false)} />}
    </div>
  );
}

function NotificationsModal({ notifications, onClose }) {
  return (
    <div className="fixed inset-0 z-[100] bg-black/50 flex items-end">
      <div className="bg-white w-full rounded-t-2xl max-h-[85vh] overflow-hidden">
        <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between sticky top-0">
          <span className="font-bold text-slate-800">Notifications</span>
          <button onClick={onClose} className="p-2 hover:bg-slate-200 rounded-full"><X size={20} /></button>
        </div>
        <div className="p-4 overflow-y-auto max-h-[65vh]">
          {notifications.length === 0 ? (
            <div className="text-center py-8 text-slate-400"><Bell size={32} className="mx-auto mb-2" /><div>No notifications</div></div>
          ) : (
            <div className="space-y-3">
              {notifications.map((n, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg">
                  <div className="text-sm font-medium text-slate-800">{n.subject || n.title}</div>
                  <div className="text-xs text-slate-500 mt-1">{n.message || n.body}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
